import { Suspense, lazy } from 'react'
import { Navigate, Outlet, useLocation, useRoutes } from 'react-router-dom'
import type { ComponentType, LazyExoticComponent, ReactNode } from 'react'
import type { RouteObject } from 'react-router-dom'
import { ConsoleFrame, ScreenError, ScreenErrorBoundary, ScreenLoading } from '@/components/app'
import { useAuthStore } from '@/store/authStore'
import { can } from '@/store/permissions'
import type { Permission } from '@/store/permissions'
import { NoAccess } from '@/screens/NoAccess/NoAccess'
import { NotFound } from '@/screens/NotFound/NotFound'

/**
 * Every address in the console, as data.
 *
 * A table rather than nested JSX, for two reasons that both come up in practice.
 * It can be read: thirty routes and their permissions fit on two screens and a
 * reviewer can check that the audit log is behind `audit.read` without tracing
 * a component tree. And it can be tested: a suite mounts this same array in a
 * `MemoryRouter` at any path, so a guard is exercised against the routes the
 * application actually ships rather than against a copy written for the test.
 *
 * Screens are lazy. A console has thirty of them and a person's first paint is
 * the login form; shipping the case timeline, the chart layer and the broadcast
 * composer to somebody who has not signed in yet is most of a megabyte spent
 * before the first useful pixel. The Suspense boundary sits inside the frame
 * rather than around it, so a first visit to a route swaps the content region
 * and leaves the navigation where it is. A boundary above the frame makes the
 * whole shell disappear and come back on every first navigation, which reads as
 * a page reload.
 *
 * Route order is not load-bearing. React Router ranks static segments above
 * dynamic ones, so `/broadcasts/new` wins over `/broadcasts/:broadcastId`
 * wherever the two are written. They are still written in that order, because
 * the next person to read this should not have to know that.
 *
 * The table itself stays inside the module and `ConsoleRoutes` is what leaves
 * it. That is not encapsulation for its own sake: a file that exports both a
 * component and a constant gives up fast refresh, and this is the file somebody
 * edits every time a screen is added. Nothing is lost by it, because mounting
 * `<ConsoleRoutes />` inside a `MemoryRouter` at any address exercises exactly
 * the same table the application ships.
 */

/**
 * Wraps a named export as a lazy default.
 *
 * `React.lazy` insists on a default export and this codebase does not have any,
 * which would otherwise mean thirty hand-written adapter closures. Typing the
 * module as `{ [P in K]: ComponentType }` means a renamed export is a
 * compile error here rather than an undefined component at the moment somebody
 * clicks the link.
 */
function lazyScreen<K extends string>(
  name: K,
  load: () => Promise<{ [P in K]: ComponentType }>,
): LazyExoticComponent<ComponentType> {
  return lazy(async () => ({ default: (await load())[name] }))
}

const Login = lazyScreen('Login', () => import('@/screens/Login/Login'))
const AcceptInvite = lazyScreen('AcceptInvite', () => import('@/screens/AcceptInvite/AcceptInvite'))
const ForgotPassword = lazyScreen(
  'ForgotPassword',
  () => import('@/screens/ForgotPassword/ForgotPassword'),
)
const ResetPassword = lazyScreen(
  'ResetPassword',
  () => import('@/screens/ResetPassword/ResetPassword'),
)

const Dashboard = lazyScreen('Dashboard', () => import('@/screens/Dashboard/Dashboard'))
const Projects = lazyScreen('Projects', () => import('@/screens/Projects/Projects'))
const ProjectDetail = lazyScreen(
  'ProjectDetail',
  () => import('@/screens/ProjectDetail/ProjectDetail'),
)
const ProjectOverview = lazyScreen(
  'ProjectOverview',
  () => import('@/screens/ProjectOverview/ProjectOverview'),
)
const ProjectKnowledge = lazyScreen(
  'ProjectKnowledge',
  () => import('@/screens/ProjectKnowledge/ProjectKnowledge'),
)
const ProjectMembers = lazyScreen(
  'ProjectMembers',
  () => import('@/screens/ProjectMembers/ProjectMembers'),
)
const ProjectCodes = lazyScreen('ProjectCodes', () => import('@/screens/ProjectCodes/ProjectCodes'))
const ProjectIndicators = lazyScreen(
  'ProjectIndicators',
  () => import('@/screens/ProjectIndicators/ProjectIndicators'),
)
const ProjectSettings = lazyScreen(
  'ProjectSettings',
  () => import('@/screens/ProjectSettings/ProjectSettings'),
)

const Conversations = lazyScreen(
  'Conversations',
  () => import('@/screens/Conversations/Conversations'),
)
const Insights = lazyScreen('Insights', () => import('@/screens/Insights/Insights'))
const InsightGaps = lazyScreen('InsightGaps', () => import('@/screens/InsightGaps/InsightGaps'))
const InsightTopics = lazyScreen(
  'InsightTopics',
  () => import('@/screens/InsightTopics/InsightTopics'),
)
const InsightRumors = lazyScreen(
  'InsightRumors',
  () => import('@/screens/InsightRumors/InsightRumors'),
)

const Cases = lazyScreen('Cases', () => import('@/screens/Cases/Cases'))
const CaseDetail = lazyScreen('CaseDetail', () => import('@/screens/CaseDetail/CaseDetail'))

const Broadcasts = lazyScreen('Broadcasts', () => import('@/screens/Broadcasts/Broadcasts'))
const BroadcastCompose = lazyScreen(
  'BroadcastCompose',
  () => import('@/screens/BroadcastCompose/BroadcastCompose'),
)
const BroadcastDetail = lazyScreen(
  'BroadcastDetail',
  () => import('@/screens/BroadcastDetail/BroadcastDetail'),
)

const FieldTasks = lazyScreen('FieldTasks', () => import('@/screens/FieldTasks/FieldTasks'))
const FieldTaskDetail = lazyScreen(
  'FieldTaskDetail',
  () => import('@/screens/FieldTaskDetail/FieldTaskDetail'),
)

const Reports = lazyScreen('Reports', () => import('@/screens/Reports/Reports'))
const Team = lazyScreen('Team', () => import('@/screens/Team/Team'))
const Audit = lazyScreen('Audit', () => import('@/screens/Audit/Audit'))
const Settings = lazyScreen('Settings', () => import('@/screens/Settings/Settings'))

/**
 * Sends anybody who is not signed in to the login screen, and remembers where
 * they were going.
 *
 * The location travels in router state rather than in a query parameter. A
 * `?next=` is a redirect target an attacker can put an external address into,
 * and the console would then be a link that shows a Namu login form and lands
 * somewhere else afterwards. Router state cannot be set from outside the
 * application, which removes the whole class.
 *
 * `replace` matters as much: without it, the login screen is pushed onto the
 * history stack and the back button after signing in returns to it.
 */
export function RequireAuth() {
  const status = useAuthStore((state) => state.status)
  const location = useLocation()

  if (status !== 'authenticated') {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />
  }

  return <Outlet />
}

interface RequirePermissionProps {
  permission: Permission
  children: ReactNode
}

/**
 * Renders a screen only for a role that holds the permission.
 *
 * This is not a security boundary and must never be treated as one: the server
 * checks the same table on every request, and it is the only thing that
 * enforces anything. What this decides is whether somebody is shown a door that
 * would not open, which is a usability question and is answered here so it is
 * answered the same way for every route.
 *
 * The refusal renders in place instead of redirecting. Reasons are on
 * `NoAccess`, and the short version is that a silent bounce loses both the
 * address and the explanation.
 */
export function RequirePermission({ permission, children }: RequirePermissionProps) {
  const allowed = useAuthStore((state) => can(state.user?.role ?? null, permission))
  return allowed ? <>{children}</> : <NoAccess />
}

/**
 * The boundaries the four public screens load and fail inside.
 *
 * They have no shell around them, so this is the whole page both while a
 * sign-in form downloads and if one of them throws. The signed-in half of the
 * table does not use this: `ConsoleFrame` holds its own pair inside the shell,
 * which is what keeps a first visit to a route from unmounting the sidebar and
 * the top bar and back again, and what leaves a person somewhere to click after
 * a screen fails.
 *
 * The error fallback here offers nothing to press, because there is nowhere to
 * send somebody who cannot get past the sign-in screen. Every link would lead
 * to the guard that sends them back to it.
 */
function PublicBoundary() {
  return (
    <ScreenErrorBoundary fallback={<ScreenError />}>
      <Suspense fallback={<ScreenLoading label="Loading" />}>
        <Outlet />
      </Suspense>
    </ScreenErrorBoundary>
  )
}

const routes: RouteObject[] = [
  {
    element: <PublicBoundary />,
    children: [
      { path: '/login', element: <Login /> },
      { path: '/invite/accept', element: <AcceptInvite /> },
      { path: '/password/forgot', element: <ForgotPassword /> },
      { path: '/password/reset', element: <ResetPassword /> },
    ],
  },

  {
    element: <RequireAuth />,
    children: [
      {
        element: <ConsoleFrame />,
        children: [
          /*
           * The root is a redirect rather than the dashboard itself, so that
           * `/dashboard` is the only address the dashboard has. Two addresses
           * for one screen means two entries in the history stack for one
           * place, and a nav item that fails to look current on one of them.
           */
          { index: true, element: <Navigate to="/dashboard" replace /> },
          { path: 'dashboard', element: <Dashboard /> },

          {
            path: 'projects',
            children: [
              {
                index: true,
                element: (
                  <RequirePermission permission="projects.read">
                    <Projects />
                  </RequirePermission>
                ),
              },
              {
                path: ':projectId',
                element: (
                  <RequirePermission permission="projects.read">
                    <ProjectDetail />
                  </RequirePermission>
                ),
                children: [
                  { index: true, element: <ProjectOverview /> },
                  {
                    path: 'knowledge',
                    element: (
                      <RequirePermission permission="knowledge.read">
                        <ProjectKnowledge />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'members',
                    element: (
                      <RequirePermission permission="members.read">
                        <ProjectMembers />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'codes',
                    element: (
                      <RequirePermission permission="codes.read">
                        <ProjectCodes />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'indicators',
                    element: (
                      <RequirePermission permission="indicators.read">
                        <ProjectIndicators />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'settings',
                    element: (
                      <RequirePermission permission="projects.update">
                        <ProjectSettings />
                      </RequirePermission>
                    ),
                  },
                ],
              },
            ],
          },

          {
            path: 'conversations',
            element: (
              <RequirePermission permission="conversations.readAggregate">
                <Conversations />
              </RequirePermission>
            ),
          },

          {
            path: 'insights',
            element: (
              <RequirePermission permission="insights.read">
                <Insights />
              </RequirePermission>
            ),
            children: [
              // Gaps, not the first tab alphabetically. It is the most valuable
              // thing the platform produces and it is what Insight should open on.
              { index: true, element: <Navigate to="gaps" replace /> },
              { path: 'gaps', element: <InsightGaps /> },
              { path: 'topics', element: <InsightTopics /> },
              { path: 'rumors', element: <InsightRumors /> },
            ],
          },

          /*
           * Cases and tasks gate on the assigned-only permission, which is the
           * weaker of the pair. A field agent has to reach their own caseload,
           * and the server decides which rows they get; gating the screen on
           * `cases.read` would lock an agent out of the work assigned to them.
           */
          {
            path: 'cases',
            children: [
              {
                index: true,
                element: (
                  <RequirePermission permission="cases.readAssigned">
                    <Cases />
                  </RequirePermission>
                ),
              },
              {
                path: ':caseId',
                element: (
                  <RequirePermission permission="cases.readAssigned">
                    <CaseDetail />
                  </RequirePermission>
                ),
              },
            ],
          },

          {
            path: 'broadcasts',
            children: [
              {
                index: true,
                element: (
                  <RequirePermission permission="broadcasts.read">
                    <Broadcasts />
                  </RequirePermission>
                ),
              },
              {
                path: 'new',
                element: (
                  <RequirePermission permission="broadcasts.write">
                    <BroadcastCompose />
                  </RequirePermission>
                ),
              },
              {
                path: ':broadcastId',
                element: (
                  <RequirePermission permission="broadcasts.read">
                    <BroadcastDetail />
                  </RequirePermission>
                ),
              },
            ],
          },

          {
            path: 'tasks',
            children: [
              {
                index: true,
                element: (
                  <RequirePermission permission="tasks.readAssigned">
                    <FieldTasks />
                  </RequirePermission>
                ),
              },
              {
                path: ':taskId',
                element: (
                  <RequirePermission permission="tasks.readAssigned">
                    <FieldTaskDetail />
                  </RequirePermission>
                ),
              },
            ],
          },

          {
            path: 'reports',
            element: (
              <RequirePermission permission="reports.export">
                <Reports />
              </RequirePermission>
            ),
          },
          {
            path: 'team',
            element: (
              <RequirePermission permission="staff.read">
                <Team />
              </RequirePermission>
            ),
          },
          {
            path: 'audit',
            element: (
              <RequirePermission permission="audit.read">
                <Audit />
              </RequirePermission>
            ),
          },
          { path: 'settings', element: <Settings /> },

          /*
           * The only catch-all, and it lives inside the authenticated frame
           * deliberately. A signed-in person who mistypes an address sees the
           * console's own not-found inside the navigation they know, rather
           * than a bare page that looks like being signed out; and a signed-out
           * visitor to a bad address is sent to the login screen by the guard
           * above, which is right, because the address they were sent may well
           * be a real one they simply cannot see yet.
           */
          { path: '*', element: <NotFound /> },
        ],
      },
    ],
  },
]

/**
 * The table, mounted.
 *
 * `useRoutes` has to be called inside a router, which is why this is a
 * component and not the array itself. The alternative, `createBrowserRouter` at
 * module scope, builds a router while the module is being imported and leaves
 * the test suite fighting a real history object it never asked for.
 */
export function ConsoleRoutes() {
  return useRoutes(routes)
}
